import React, {useMemo, useState, useEffect, useCallback} from "react"
import {Card, Col, Container, Form, Row, Button} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {mockRules} from "../../../data/rules";
import {Link} from "react-router-dom";
import BreadCrumb from "../../../Common/BreadCrumb";
import DataTable from "../../../Common/DataTable";
import {mockScenarios} from "../../../data/scenarios";
import {EditRule} from "../Components/EditRule";
import {DeleteRule} from "../Components/DeleteRule";
import {DeleteAlerts} from "../Components/DeleteAlerts";
import {dashboardAPI} from "../../../services/api";


const RuleList = () => {
    const {t} = useTranslation();
    document.title = t('RuleList.title');

    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [data, setData] = useState<Rule[]>(mockRules);
    const [scenarios, setScenarios] = useState<Scenario[]>(mockScenarios);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteAlertsModalOpen, setDeleteAlertsModalOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch scenarios for scenario name lookup
    useEffect(() => {
        const fetchScenarios = async () => {
            if (isDemoUser) {
                setScenarios(mockScenarios);
                return;
            }
            try {
                const response: any = await dashboardAPI.getScenarios();
                const scenariosData = Array.isArray(response) ? response : (response?.data || []);
                setScenarios(scenariosData.map((s: any) => ({
                    id: s.id || '',
                    name: s.name || ''
                })));
            } catch (error) {
                console.error('Failed to fetch scenarios:', error);
            }
        };
        fetchScenarios();
    }, [isDemoUser]);

    // Fetch rules from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use mock data for demo users
            setData(mockRules);
            return;
        }

        const fetchRules = async () => {
            setIsLoading(true);
            try {
                console.log('📋 Fetching rules from backend...');
                const response: any = await dashboardAPI.getRules();
                
                // Normalize response - handle both AxiosResponse and direct data
                const rules = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Rules received:', rules.length, 'rules');
                
                // Map backend RuleDTO to frontend Rule format
                const mappedRules: Rule[] = rules.map((rule: any) => {
                    // Parse conditionTreeJson to extract conditionGroup
                    let conditionGroup: ConditionGroup = { join: 'and', conditions: [] };
                    if (rule.conditionTreeJson) {
                        try {
                            conditionGroup = treeJsonToConditionGroup(rule.conditionTreeJson);
                        } catch (e) {
                            console.warn('⚠️ Failed to parse conditionTreeJson:', e);
                        }
                    }
                    
                    // Format date (backend returns LocalDateTime, convert to string)
                    const createdOn = rule.createdOn 
                        ? (typeof rule.createdOn === 'string' 
                            ? rule.createdOn.split('T')[0] 
                            : new Date(rule.createdOn).toISOString().split('T')[0])
                        : new Date().toISOString().split('T')[0];
                    
                    return {
                        id: rule.id || '',
                        name: rule.name || '',
                        active: rule.active !== false,
                        scenarioId: rule.scenarioId || '',
                        createdOn: createdOn,
                        description: rule.description || '',
                        alertCount: 0, // Backend doesn't provide this, will need to fetch separately if needed
                        conditionGroup: conditionGroup
                    };
                });
                
                setData(mappedRules);
            } catch (error: any) {
                console.error('❌ Failed to fetch rules:', error);
                // Fall back to empty array on error
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRules();
    }, [isDemoUser]);

    // Helper function to convert backend conditionTreeJson to frontend conditionGroup
    const treeJsonToConditionGroup = (treeJson: string): ConditionGroup => {
        if (!treeJson) {
            return { join: 'and', conditions: [] };
        }

        try {
            const tree = JSON.parse(treeJson);
            const convertNode = (node: any): any => {
                if (node.type === 'CONDITION' && node.condition) {
                    return {
                        field: node.condition.field,
                        operator: node.condition.operator,
                        value: node.condition.value
                    };
                }

                if (node.conditions && Array.isArray(node.conditions)) {
                    const join = node.type === 'OR' ? 'or' : 'and';
                    return {
                        join: join,
                        conditions: node.conditions.map((child: any) => convertNode(child))
                    };
                }

                return { join: 'and', conditions: [] };
            };

            return convertNode(tree);
        } catch (e) {
            console.error('Failed to parse conditionTreeJson:', e);
            return { join: 'and', conditions: [] };
        }
    };

    const handleEdit = (rule: Rule) => {
        setSelectedRule(rule);
        setEditModalOpen(true);
    };

    const handleDelete = (rule: Rule) => {
        setSelectedRule(rule);
        setDeleteModalOpen(true);
    };
    const handleAlertsDelete = (rule: Rule) => {
        setSelectedRule(rule);
        setDeleteAlertsModalOpen(true);
    };

    const handleToggleActive = useCallback(async (rule: Rule, newActive: boolean) => {
        // Update locally first for immediate feedback
        setData(prevData => {
            const newData = [...prevData];
            const index = newData.findIndex(r => r.id === rule.id);
            if (index !== -1) {
                newData[index].active = newActive;
            }
            return newData;
        });
        
        // Update on backend for real users
        if (!isDemoUser) {
            try {
                await dashboardAPI.updateRule(rule.id, {
                    ...rule,
                    active: newActive
                });
                console.log('✅ Rule active status updated');
            } catch (error) {
                console.error('❌ Failed to update rule:', error);
                // Revert on error
                setData(prevData => {
                    const newData = [...prevData];
                    const index = newData.findIndex(r => r.id === rule.id);
                    if (index !== -1) {
                        newData[index].active = !newActive;
                    }
                    return newData;
                });
                alert('Failed to update rule status');
            }
        }
    }, [isDemoUser]);

    const columns = useMemo(
        () => {
            const cols: any[] = [
                {
                    Header: t('RuleList.rule_name'),
                    accessor: "name",
                    Cell: ({row, value}: any) => {
                        const rule = data[row.index];
                        return (
                            <div style={{minWidth: 0}}>
                                <Link 
                                    to={`/rule/${rule.id}`} 
                                    style={{color: "#1976d2", textDecoration: "none", wordBreak: 'break-word'}}
                                >
                                    {value}
                                </Link>
                                {isMobile && (
                                    <div className="mt-1">
                                        <small className="text-muted">ID: {rule.id}</small>
                                    </div>
                                )}
                            </div>
                        );
                    }
                },
                {
                    Header: t('RuleList.scenario_name'),
                    accessor: "scenarioId",
                    Cell: ({value}: any) => {
                        const scenario = scenarios.find((s) => s.id === value);
                        const scenarioName = scenario?.name || value;
                        return (
                            <Link 
                                to={`/scenario/${value}`} 
                                style={{color: "#1976d2", textDecoration: "none", wordBreak: 'break-word'}}
                            >
                                {scenarioName}
                            </Link>
                        )
                    }
                },
            ];
            
            // Add rule_id and alert_count only on desktop
            if (!isMobile) {
                cols.unshift({
                    Header: t('RuleList.rule_id'),
                    accessor: "id",
                    Cell: ({value}: any) => (
                        <Link to={`/rule/${value}`} style={{color: "#1976d2", textDecoration: "none"}}>
                            {value}
                        </Link>
                    ),
                });
                cols.push({
                    Header: t('RuleList.alert_count'),
                    accessor: "alertCount",
                });
            }
            
            cols.push(
                {
                    Header: t('RuleList.active'),
                    accessor: "active",
                    Cell: ({row, value}: any) => {
                        const rule = data[row.index];
                        return (
                            <div className="form-check form-switch form-switch-custom form-switch-primary">
                                <Form.Check 
                                    onChange={() => handleToggleActive(rule, !value)} 
                                    type="checkbox" 
                                    role="switch" 
                                    id={`SwitchCheck-${row.index}`} 
                                    checked={value}
                                />
                            </div>
                        );
                    },
                }
            );
            
            // Add created_on only on desktop
            if (!isMobile) {
                cols.push({
                    Header: t('RuleList.created_on'),
                    accessor: "createdOn",
                });
            }
            
            return cols;
        },
        [data, t, scenarios, isDemoUser, isMobile, handleToggleActive]
    );



    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header'), path: "/"},
                            {label: t('RuleList.page_title'), path: "/rules"}
                        ]}
                    />
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <Card.Body>
                                    <div className={`${isMobile ? 'mb-3' : 'float-end'}`}>
                                        <Link 
                                            to="/rule-add" 
                                            className={`btn btn-primary btn-label ${isMobile ? 'w-100' : ''}`}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <div className="flex-shrink-0">
                                                    <i className="ri-file-add-fill label-icon align-middle fs-lg me-2"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    {t('RuleList.add_rule')}
                                                </div>
                                            </div>
                                        </Link>
                                    </div>

                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : isMobile ? (
                                        // Mobile card view with pagination
                                        <>
                                            <div className="d-flex flex-column gap-3">
                                                {data.length === 0 ? (
                                                    <div className="text-center py-5 text-muted">
                                                        {t('RuleList.no_rules') || 'No rules found'}
                                                    </div>
                                                ) : (
                                                    data
                                                        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                                        .map((rule) => {
                                                    const scenario = scenarios.find((s) => s.id === rule.scenarioId);
                                                    const scenarioName = scenario?.name || rule.scenarioId;
                                                    return (
                                                        <Card key={rule.id} className="shadow-sm">
                                                            <Card.Body>
                                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                                    <div className="flex-grow-1" style={{minWidth: 0}}>
                                                                        <Link 
                                                                            to={`/rule/${rule.id}`} 
                                                                            style={{color: "#1976d2", textDecoration: "none"}}
                                                                        >
                                                                            <h6 className="mb-1" style={{wordBreak: 'break-word'}}>
                                                                                {rule.name}
                                                                            </h6>
                                                                        </Link>
                                                                        <small className="text-muted d-block mb-2">
                                                                            ID: {rule.id}
                                                                        </small>
                                                                        <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                                                                            <Link 
                                                                                to={`/scenario/${rule.scenarioId}`} 
                                                                                style={{color: "#1976d2", textDecoration: "none"}}
                                                                            >
                                                                                <span className="badge bg-info">
                                                                                    {scenarioName}
                                                                                </span>
                                                                            </Link>
                                                                            <span className="badge bg-secondary">
                                                                                {t('RuleList.alert_count')}: {rule.alertCount || 0}
                                                                            </span>
                                                                            <div className="form-check form-switch form-switch-custom form-switch-primary">
                                                                                <Form.Check 
                                                                                    onChange={() => handleToggleActive(rule, !rule.active)} 
                                                                                    type="checkbox" 
                                                                                    role="switch" 
                                                                                    id={`rule-switch-mobile-${rule.id}`} 
                                                                                    checked={rule.active}
                                                                                    label={<small>{t('RuleList.active') || 'Active'}</small>}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        {rule.createdOn && (
                                                                            <small className="text-muted d-block mt-2">
                                                                                📅 {rule.createdOn}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEdit(rule)}
                                                                        className="w-100"
                                                                    >
                                                                        {t('RuleList.edit') || 'Edit'}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleDelete(rule)}
                                                                        className="w-100"
                                                                    >
                                                                        {t('RuleList.delete') || 'Delete'}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        onClick={() => handleAlertsDelete(rule)}
                                                                        className="w-100"
                                                                    >
                                                                        {t('RuleList.delete_alerts') || 'Delete Alerts'}
                                                                    </Button>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    );
                                                        })
                                                )}
                                            </div>
                                            {data.length > pageSize && (
                                                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                                    <div className="text-muted small">
                                                        {t('RuleList.showing') || 'Showing'} {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, data.length)} {t('RuleList.of') || 'of'} {data.length}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                            disabled={currentPage === 1}
                                                        >
                                                            {t('RuleList.previous') || 'Previous'}
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(data.length / pageSize), prev + 1))}
                                                            disabled={currentPage >= Math.ceil(data.length / pageSize)}
                                                        >
                                                            {t('RuleList.next') || 'Next'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Desktop table view
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data}
                                                title={t('RuleList.search_rule')}
                                                editable={true}
                                                deletable={true}
                                                alertsDeletable={true}
                                                onEdit={handleEdit}
                                                onDelete={handleDelete}
                                                onAlertsDelete={handleAlertsDelete}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <EditRule
                        show={editModalOpen}
                        rule={selectedRule}
                        onClose={() => setEditModalOpen(false)}
                        onSave={async (updatedRule: any) => {
                            if (!selectedRule) return;
                            
                            try {
                                if (isDemoUser) {
                                    // Update local state for demo
                                    const newData = data.map(r => r.id === selectedRule.id ? updatedRule : r);
                                    setData(newData);
                                } else {
                                    // Update on backend
                                    await dashboardAPI.updateRule(selectedRule.id, updatedRule);
                                    // Refresh rules list
                                    const response: any = await dashboardAPI.getRules();
                                    const rules = Array.isArray(response) ? response : (response?.data || []);
                                    const mappedRules: Rule[] = rules.map((rule: any) => {
                                        let conditionGroup: ConditionGroup = { join: 'and', conditions: [] };
                                        if (rule.conditionTreeJson) {
                                            try {
                                                conditionGroup = treeJsonToConditionGroup(rule.conditionTreeJson);
                                            } catch (e) {
                                                console.warn('⚠️ Failed to parse conditionTreeJson:', e);
                                            }
                                        }
                                        const createdOn = rule.createdOn 
                                            ? (typeof rule.createdOn === 'string' 
                                                ? rule.createdOn.split('T')[0] 
                                                : new Date(rule.createdOn).toISOString().split('T')[0])
                                            : new Date().toISOString().split('T')[0];
                                        return {
                                            id: rule.id || '',
                                            name: rule.name || '',
                                            active: rule.active !== false,
                                            scenarioId: rule.scenarioId || '',
                                            createdOn: createdOn,
                                            description: rule.description || '',
                                            alertCount: 0,
                                            conditionGroup: conditionGroup
                                        };
                                    });
                                    setData(mappedRules);
                                }
                                setEditModalOpen(false);
                            } catch (error) {
                                console.error('❌ Failed to update rule:', error);
                                alert('Failed to update rule');
                            }
                        }}
                    />

                    <DeleteRule
                        show={deleteModalOpen}
                        rule={selectedRule}
                        onClose={() => setDeleteModalOpen(false)}
                        onConfirm={async () => {
                            if (!selectedRule) return;
                            
                            try {
                                if (isDemoUser) {
                                    // Delete from local state for demo
                                    const newData = data.filter(r => r.id !== selectedRule.id);
                                    setData(newData);
                                } else {
                                    // Delete from backend
                                    await dashboardAPI.deleteRule(selectedRule.id);
                                    // Refresh rules list
                                    const response: any = await dashboardAPI.getRules();
                                    const rules = Array.isArray(response) ? response : (response?.data || []);
                                    const mappedRules: Rule[] = rules.map((rule: any) => {
                                        let conditionGroup: ConditionGroup = { join: 'and', conditions: [] };
                                        if (rule.conditionTreeJson) {
                                            try {
                                                conditionGroup = treeJsonToConditionGroup(rule.conditionTreeJson);
                                            } catch (e) {
                                                console.warn('⚠️ Failed to parse conditionTreeJson:', e);
                                            }
                                        }
                                        const createdOn = rule.createdOn 
                                            ? (typeof rule.createdOn === 'string' 
                                                ? rule.createdOn.split('T')[0] 
                                                : new Date(rule.createdOn).toISOString().split('T')[0])
                                            : new Date().toISOString().split('T')[0];
                                        return {
                                            id: rule.id || '',
                                            name: rule.name || '',
                                            active: rule.active !== false,
                                            scenarioId: rule.scenarioId || '',
                                            createdOn: createdOn,
                                            description: rule.description || '',
                                            alertCount: 0,
                                            conditionGroup: conditionGroup
                                        };
                                    });
                                    setData(mappedRules);
                                }
                                setDeleteModalOpen(false);
                            } catch (error) {
                                console.error('❌ Failed to delete rule:', error);
                                alert('Failed to delete rule');
                            }
                        }}
                    />
                    <DeleteAlerts
                        show={deleteAlertsModalOpen}
                        rule={selectedRule}
                        onClose={() => setDeleteAlertsModalOpen(false)}
                        onConfirm={() => {
                            // delete from state
                            setDeleteAlertsModalOpen(false);
                        }}
                    />
                </Container>
            </div>
        </React.Fragment>
    )
}

export default RuleList